// Interpolation buffer for remote players (client side, but Phaser-free so
// it can be unit tested and ported). See docs/netcode.md "Remote players".
//
// The server sends positions one snapshot interval apart. Rather than easing
// toward the newest one (rubber-band paths, a step per packet, freezes on
// loss), the client renders remote entities `delayTicks` in the past
// and slides them along the straight line between the two snapshots that
// bracket that render time. Everything on screen is somewhere the player
// really was, just a little late.
// Timing the game runs at; the interpolator derives its windows from it.
export interface NetTiming {
  tickRate: number; // simulation ticks per second
  snapshotEveryTicks: number; // one snapshot every N ticks
}

export interface InterpOptions {
  // Render delay in snapshot intervals (default 2: one late or lost packet
  // is absorbed without a hitch; 100 ms at 20 Hz snapshots).
  delaySnapshots?: number;
  // Keep extrapolating along the last velocity for at most this many
  // snapshot intervals when the buffer runs dry (default 2), then hold.
  maxExtrapolateSnapshots?: number;
  // A jump between consecutive samples larger than this is a teleport
  // (respawn, restart): snap instead of sliding (default 64 px).
  teleportPx?: number;
}

// Samples kept per entity (~0.8 s at 20 Hz).
const HISTORY = 16;

export interface InterpOut {
  x: number;
  y: number;
  aim: number;
  // Debug: true when the sample was extrapolated (buffer ran dry).
  extrapolated: boolean;
}

export class Interpolator {
  private readonly max: number;
  private readonly msPerTick: number;
  readonly delayTicks: number;
  readonly maxExtrapolateTicks: number;
  private readonly teleportPx2: number;
  // Ring per player (struct-of-arrays, no allocation after construction).
  private readonly tick: Float64Array;
  private readonly x: Float32Array;
  private readonly y: Float32Array;
  private readonly aim: Float32Array;
  private readonly head: Uint8Array; // index of the newest sample
  private readonly count: Uint8Array;

  // Estimated server clock: serverTick ≈ localMs / msPerTick + offset.
  // The offset is smoothed over arrivals so network jitter doesn't shake the
  // render clock; a big disagreement (reconnect, stall) snaps it.
  private offset = Number.NaN;
  private newestTick = 0;

  constructor(maxEntities: number, timing: NetTiming, opts: InterpOptions = {}) {
    this.max = maxEntities;
    this.msPerTick = 1000 / timing.tickRate;
    this.delayTicks = (opts.delaySnapshots ?? 2) * timing.snapshotEveryTicks;
    this.maxExtrapolateTicks = (opts.maxExtrapolateSnapshots ?? 2) * timing.snapshotEveryTicks;
    const tp = opts.teleportPx ?? 64;
    this.teleportPx2 = tp * tp;
    this.tick = new Float64Array(maxEntities * HISTORY);
    this.x = new Float32Array(maxEntities * HISTORY);
    this.y = new Float32Array(maxEntities * HISTORY);
    this.aim = new Float32Array(maxEntities * HISTORY);
    this.head = new Uint8Array(maxEntities);
    this.count = new Uint8Array(maxEntities);
  }

  // Call once per snapshot with the server tick it carries and the local
  // time (ms) it arrived.
  onSnapshot(tick: number, nowMs: number): void {
    const observed = tick - nowMs / this.msPerTick;
    if (Number.isNaN(this.offset) || Math.abs(observed - this.offset) > 4 * this.delayTicks) {
      this.offset = observed;
    } else {
      // Bias toward the latest arrival a little faster when it says the
      // server is ahead of our estimate (packets are never early, so a
      // higher observed offset is better information than a lower one).
      const k = observed > this.offset ? 0.2 : 0.05;
      this.offset += (observed - this.offset) * k;
    }
    if (tick > this.newestTick) this.newestTick = tick;
  }

  // Record one player's state from a snapshot. Out-of-order or duplicate
  // ticks are ignored.
  push(id: number, tick: number, x: number, y: number, aim: number): void {
    const base = id * HISTORY;
    const n = this.count[id]!;
    if (n > 0) {
      const h = base + this.head[id]!;
      const lastTick = this.tick[h]!;
      if (tick <= lastTick) return;
      const dx = x - this.x[h]!;
      const dy = y - this.y[h]!;
      if (dx * dx + dy * dy > this.teleportPx2) this.count[id] = 0; // teleport: start over
    }
    const next = n === 0 ? 0 : (this.head[id]! + 1) % HISTORY;
    const i = base + next;
    this.tick[i] = tick;
    this.x[i] = x;
    this.y[i] = y;
    this.aim[i] = aim;
    this.head[id] = next;
    if (this.count[id]! < HISTORY) this.count[id] = this.count[id]! + 1;
  }

  clear(id: number): void {
    this.count[id] = 0;
  }

  has(id: number): boolean {
    return this.count[id]! > 0;
  }

  // The server tick being drawn at local time nowMs.
  renderTick(nowMs: number): number {
    if (Number.isNaN(this.offset)) return 0;
    return nowMs / this.msPerTick + this.offset - this.delayTicks;
  }

  // Milliseconds of buffered future the newest sample gives us at nowMs
  // (positive = healthy; negative = extrapolating). For the debug line.
  headroomMs(nowMs: number): number {
    return (this.newestTick - this.renderTick(nowMs)) * this.msPerTick;
  }

  // Resolve one player's position at render tick `rt` into `out`. Returns
  // false if there are no samples for this player.
  sample(id: number, rt: number, out: InterpOut): boolean {
    const n = this.count[id]!;
    if (n === 0) return false;
    const base = id * HISTORY;
    const head = this.head[id]!;
    const newest = base + head;
    out.extrapolated = false;

    // Newest sample is already older than the render time: extrapolate a
    // little along the last segment, then hold.
    if (rt >= this.tick[newest]!) {
      out.x = this.x[newest]!;
      out.y = this.y[newest]!;
      out.aim = this.aim[newest]!;
      if (n >= 2 && rt > this.tick[newest]!) {
        const prev = base + ((head + HISTORY - 1) % HISTORY);
        const span = this.tick[newest]! - this.tick[prev]!;
        const ahead = Math.min(rt - this.tick[newest]!, this.maxExtrapolateTicks);
        if (span > 0 && ahead > 0) {
          const f = ahead / span;
          out.x += (this.x[newest]! - this.x[prev]!) * f;
          out.y += (this.y[newest]! - this.y[prev]!) * f;
          out.extrapolated = true;
        }
      }
      return true;
    }

    // Walk back from the newest sample to find the pair bracketing rt.
    let b = newest;
    for (let k = 1; k < n; k++) {
      const a = base + ((head + HISTORY - k) % HISTORY);
      const ta = this.tick[a]!;
      const tb = this.tick[b]!;
      if (rt >= ta) {
        const f = tb > ta ? (rt - ta) / (tb - ta) : 1;
        out.x = this.x[a]! + (this.x[b]! - this.x[a]!) * f;
        out.y = this.y[a]! + (this.y[b]! - this.y[a]!) * f;
        out.aim = lerpAngle(this.aim[a]!, this.aim[b]!, f);
        return true;
      }
      b = a;
    }
    // Render time is older than everything we have (just appeared): oldest.
    out.x = this.x[b]!;
    out.y = this.y[b]!;
    out.aim = this.aim[b]!;
    return true;
  }
}

// Shortest-arc interpolation between two angles in radians.
export function lerpAngle(a: number, b: number, f: number): number {
  let d = b - a;
  while (d > Math.PI) d -= Math.PI * 2;
  while (d < -Math.PI) d += Math.PI * 2;
  return a + d * f;
}
